/* ────────────────────────────────────────────────────────────────────
 * Legal content (English) — Privacy Policy · Terms of Service · Sub-processors.
 *
 * Source of truth: docs/legal-review/2026-07-11-prinotes-legal-pages-v1.1-draft.md
 * (advisor-approved-as-drafted, all owner PENDINGs resolved 2026-07-11).
 * Only the three clean documents are ported here — no cover, appendix, owner
 * notes, or the "Arabic version prevails" bilingual banner (that banner may not
 * ship English-only; add it when Arabic goes live). Arabic follows owner review
 * of this English version.
 *
 * Inline markup supported by the renderer: **bold** and [label](href).
 * Emails are mailto: links; cross-references between the three docs are handled
 * by the sibling nav at the foot of each page.
 * ──────────────────────────────────────────────────────────────────── */

export type LegalBlock =
  | { t: 'p'; c: string }
  | { t: 'h'; c: string }
  | { t: 'ul' | 'ol'; c: string[] }
  | { t: 'table'; head: string[]; rows: string[][] }
  | { t: 'note'; c: string; label?: string };

export interface LegalSection {
  id: string;
  n: string;
  title: string;
  blocks: LegalBlock[];
}

export interface LegalDoc {
  key: 'privacy' | 'terms' | 'subprocessors';
  eyebrow: string;
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
}

/* The National Number is deliberately NOT repeated here: it lives on the About
 * page only (owner rule 2026-07-14), which carries the E-Commerce Law
 * disclosures. Guarded by legal-pages.spec.ts. */
const IDENTITY =
  '**Priyora Company** (شركة برييورا) · Jeddah 23532, Kingdom of Saudi Arabia.';

const PRIVACY: LegalDoc = {
  key: 'privacy',
  eyebrow: 'Legal',
  title: 'Privacy Policy',
  updated: 'Last updated 28 July 2026',
  intro:
    'This Privacy Policy explains how Priyora Company collects, uses, stores, shares, and protects your personal data when you use Prinotes, our meeting-notes service, including the Prinotes website (prinotes.com) and application (app.prinotes.com). We are committed to protecting your privacy in line with the Saudi Personal Data Protection Law (PDPL) and its Implementing Regulations. Our guiding principle is simple: your data is yours.',
  sections: [
    {
      id: 'introduction',
      n: '01',
      title: 'Introduction & Scope',
      blocks: [
        {
          t: 'p',
          c: 'Prinotes is a service operated by **Priyora Company**, a company registered in the Kingdom of Saudi Arabia. In this Policy, "Prinotes" refers to the product, and "Priyora", "we", "us", or "our" refers to Priyora Company, the entity responsible for your personal data (the **data controller**).',
        },
        {
          t: 'p',
          c: 'This Policy applies to everyone who visits our website or uses our application (together, the "Service"), whether you are an individual user or a member of an organization that uses Prinotes.',
        },
        {
          t: 'p',
          c: 'By using the Service, you acknowledge that you have read and understood this Policy. Where the law requires your consent for a specific activity, we ask for it separately and clearly, and you are never asked to agree to unrelated uses in a single step.',
        },
        {
          t: 'p',
          c: 'We design Prinotes to collect the **minimum data needed** to provide the Service, and to keep it only where it directly benefits you.',
        },
      ],
    },
    {
      id: 'who-we-are',
      n: '02',
      title: 'Who We Are (Data Controller)',
      blocks: [
        { t: 'p', c: 'The data controller responsible for your personal data is:' },
        {
          t: 'ul',
          c: [
            '**Priyora Company** (شركة برييورا), a single-member Limited Liability Company registered in the Kingdom of Saudi Arabia.',
            '**Registered in:** Jeddah 23532, Kingdom of Saudi Arabia.',
            '**Registered with:** the National Register for Personal Data Protection (NDGP, SDAIA), registration No. **3260007171**.',
          ],
        },
        {
          t: 'p',
          c: 'For any question about this Policy, or to exercise your rights, contact our privacy team at [privacy@prinotes.com](mailto:privacy@prinotes.com)',
        },
        {
          t: 'p',
          c: 'For general help, contact [support@prinotes.com](mailto:support@prinotes.com)',
        },
        {
          t: 'p',
          c: 'Where required by law, we will appoint a Data Protection Officer.',
        },
      ],
    },
    {
      id: 'definitions',
      n: '03',
      title: 'Definitions',
      blocks: [
        {
          t: 'ul',
          c: [
            '**Personal Data**: any information that identifies you or could reasonably be linked to you, such as your name, email address, or voice.',
            '**Meeting Content**: the audio you record or upload, the transcripts we generate, and the summaries, minutes, decisions, and action items produced from them.',
            '**Processing**: any operation performed on personal data, such as collecting, storing, using, sharing, or deleting it.',
            '**Sub-processor**: a third-party service that processes personal data on our behalf to help us provide the Service.',
            '**Service**: the Prinotes website and application, together with all related features.',
            "**PDPL**: the Saudi Personal Data Protection Law, the Kingdom's data-protection law regulated and enforced by the Saudi Data & Artificial Intelligence Authority ([SDAIA](https://sdaia.gov.sa)).",
            '[**NDGP**](https://dgp.sdaia.gov.sa): the National Data Governance Platform, operated by the Saudi Data & Artificial Intelligence Authority ([SDAIA](https://sdaia.gov.sa)), which hosts the National Register for Personal Data Protection.',
          ],
        },
      ],
    },
    {
      id: 'what-we-collect',
      n: '04',
      title: 'What Information We Collect',
      blocks: [
        { t: 'h', c: 'Account & identity data' },
        {
          t: 'p',
          c: 'You sign in with your **Google or Microsoft account**. Prinotes does not use passwords. When you sign in, we receive your name, email address, profile picture, and the account identifier from your provider.',
        },
        { t: 'h', c: 'Meeting Content' },
        {
          t: 'p',
          c: 'When you record or upload a meeting, we process the **audio**, the **transcript** we generate from it, and the **summaries, minutes, decisions, and action items** we extract. This also includes meeting details you provide, such as titles, dates, and participant names.',
        },
        {
          t: 'p',
          c: '**By default, the audio is deleted from our systems as soon as processing finishes**: we keep only the transcript and the notes generated from it. Audio is kept only if you have turned on audio retention (see Section 12). If you have connected Drive or OneDrive, a copy of the audio is saved to your own cloud storage and is not affected by this deletion (see Section 10).',
        },
        { t: 'h', c: 'Integration data (only when you connect)' },
        {
          t: 'p',
          c: 'If you connect **Google Workspace** or **Microsoft 365**, we access only what is needed for the features you enable, such as calendar events, tasks, files created through Prinotes, and contacts read to suggest attendees. Where you grant Drive or OneDrive access, **your meeting audio and transcripts are saved to your own cloud storage automatically unless you turn that off** (see Section 10). You can disconnect at any time.',
        },
        { t: 'h', c: 'Billing data' },
        {
          t: 'p',
          c: 'Prinotes offers a free plan and paid plans. If you subscribe to a paid plan, payments are handled by **Moyasar**, our payment provider in Saudi Arabia. **Prinotes never receives or stores your full card number**: we store only limited details such as the card brand, the last four digits, and your subscription and invoice history.',
        },
        { t: 'h', c: 'Technical & usage data' },
        {
          t: 'p',
          c: 'To operate and secure the Service, we process technical information such as your device and browser type, IP address, log data, and the essential cookies described in Section 14.',
        },
        {
          t: 'p',
          c: '**What is required and what is optional.** Your sign-in data and, when you use the core features, your meeting content are necessary to provide the Service: without them we cannot create your account or process a meeting. Connecting Google Workspace or Microsoft 365, and receiving marketing messages, are entirely optional: declining them only means those features or messages are unavailable, and nothing else.',
        },
      ],
    },
    {
      id: 'how-we-use',
      n: '05',
      title: 'How We Use Your Information & Legal Basis',
      blocks: [
        {
          t: 'p',
          c: 'We use your personal data for the purposes below. Under the PDPL, each purpose relies on a specific legal basis:',
        },
        {
          t: 'table',
          head: ['Purpose', 'Legal basis under the PDPL'],
          rows: [
            [
              'Provide the Service: record, transcribe, summarize, and sync your meetings',
              'Implementation of the agreement to which you are a party (Art. 6(2))',
            ],
            [
              'Authenticate you and keep your account secure',
              'Implementation of the agreement to which you are a party (Art. 6(2))',
            ],
            [
              'Send you service and transactional messages',
              'Implementation of the agreement to which you are a party (Art. 6(2))',
            ],
            [
              'Maintain, improve, and protect the reliability and security of the Service',
              'Legitimate interest (Art. 6(4)), never involving sensitive data, and always within your reasonable expectations',
            ],
            ['Comply with legal, accounting, and tax obligations', 'Processing pursuant to law (Art. 6(2))'],
            ['Send marketing or promotional messages', 'Your separate, opt-in consent (Art. 5)'],
          ],
        },
        {
          t: 'note',
          c: '**Your consent, your control:** We ask for consent separately for each purpose that requires it, and never bundle unrelated purposes together. You can withdraw consent at any time, and it is as easy to withdraw as it was to give.',
        },
        {
          t: 'p',
          c: 'You can withdraw any consent at any time from your account settings or by emailing [privacy@prinotes.com](mailto:privacy@prinotes.com); withdrawal takes effect without undue delay and does not affect processing that relies on another legal basis.',
        },
      ],
    },
    {
      id: 'ai-processing',
      n: '06',
      title: 'AI Processing',
      blocks: [
        {
          t: 'p',
          c: 'Prinotes relies on artificial intelligence for its core features: converting speech to text (transcription) and generating summaries, minutes, and action items from your meetings.',
        },
        {
          t: 'p',
          c: 'To do this, we use trusted third-party AI providers, currently **OpenAI** and **Google Gemini**, for transcription, structuring, and summaries. These are listed on our Sub-processors page.',
        },
        {
          t: 'note',
          c: "**We do not train AI on your content:** Your content is **not** used to train AI models, neither ours nor our AI providers'. We use these providers on paid tiers that do not train on customer content.",
        },
        {
          t: 'p',
          c: 'Our AI providers may retain content for a short period (approximately 30 days*) **solely to detect abuse and keep their services safe**, after which it is deleted. They do not use it to train their models, and we do not retain or use your raw audio to train or improve our own models.',
        },
        {
          t: 'p',
          c: '* Based on the published API data-usage policies of our AI providers (OpenAI and Google Gemini, paid API tiers), which do not use customer content for training. Exact windows are set by each provider and may change; see our Sub-processors page.',
        },
        {
          t: 'p',
          c: 'AI output can be **inaccurate or incomplete** and should be reviewed before you rely on it. See our Terms of Service for details. We do not make decisions that produce legal or similarly significant effects about you based solely on automated processing.',
        },
      ],
    },
    {
      id: 'recording',
      n: '07',
      title: 'Recording & Your Responsibility',
      blocks: [
        {
          t: 'p',
          c: 'You are in control of what you record. Prinotes records on **your own device** (microphone and/or system audio) or processes recordings you upload.',
        },
        {
          t: 'note',
          c: '**Important:** Prinotes does **not** notify other participants or obtain their consent on your behalf. **You are responsible** for informing participants and obtaining any consent required by law before recording, transcribing, or processing a meeting.',
        },
        {
          t: 'p',
          c: 'Recording conversations is regulated in many places, and some jurisdictions require the consent of **all** participants. Please review Section 5 (Recording Consent) of our Terms of Service, which sets out your responsibilities in full.',
        },
      ],
    },
    {
      id: 'meeting-participant',
      n: '08',
      title: 'If You Are a Meeting Participant (Not a Prinotes User)',
      blocks: [
        {
          t: 'p',
          c: 'A Prinotes user may record or upload a meeting in which you took part. In that case, we process the meeting audio, the transcript, and the content generated from them, which may include your voice, your name, and things you said, in order to provide the Service to that user, on the basis of our legitimate interest in operating the Service under the PDPL. The user who records the meeting is responsible for informing you and obtaining any consent required by law before recording.',
        },
        {
          t: 'p',
          c: 'We do not use meeting content to identify anyone biometrically, and we do not use it to train AI models. **The meeting audio is deleted from our systems as soon as processing finishes**, and is kept only if the account holder has turned on audio retention. The transcript and the notes generated from it are kept until the account holder deletes them (see Section 12).',
        },
        {
          t: 'p',
          c: "Even if you are not a Prinotes user, you have the rights described in Section 13 for your personal data, including access, correction, and deletion. Contact [privacy@prinotes.com](mailto:privacy@prinotes.com); to protect everyone's data we may need to verify your identity and involve the account holder to locate the relevant content.",
        },
      ],
    },
    {
      id: 'how-we-share',
      n: '09',
      title: 'How We Share Information',
      blocks: [
        {
          t: 'p',
          c: '**We do not sell your personal data**, and we do not use your meeting content for advertising.',
        },
        { t: 'p', c: 'We share personal data only in these situations:' },
        {
          t: 'ul',
          c: [
            '**With sub-processors** who help us run the Service, under contracts that require them to protect your data and use it only on our instructions. A summary is below, with the full list on our Sub-processors page.',
            '**To comply with the law**, respond to lawful requests, enforce our Terms, or protect the rights, safety, and security of our users and the public.',
            '**In a corporate transaction** (such as a merger or acquisition), where your data continues to be protected under this Policy.',
          ],
        },
        {
          t: 'table',
          head: ['Sub-processor', 'Purpose', 'Location'],
          rows: [
            ['OpenAI', 'AI processing of meeting content', 'United States'],
            ['Google Gemini', 'AI processing of meeting content', 'United States / global'],
            ['Oracle Cloud Infrastructure', 'Hosting & encrypted storage', 'Saudi Arabia'],
            ['Cloudflare', 'Content delivery & security', 'United States / global'],
            ['Sentry', 'Error monitoring', 'European Union / United States'],
            ['Moyasar', 'Payment processing for paid plans', 'Saudi Arabia'],
          ],
        },
        {
          t: 'p',
          c: 'See our full, up-to-date list, including Google Workspace and Microsoft 365 (used only when you connect them), on our Sub-processors page.',
        },
      ],
    },
    {
      id: 'integrations',
      n: '10',
      title: 'Google & Microsoft Integrations',
      blocks: [
        {
          t: 'p',
          c: 'If you connect your Google or Microsoft account, we request only the access needed for the features you turn on:',
        },
        {
          t: 'ul',
          c: [
            '**Calendar**: to read, create, and manage the events created through Prinotes for your meetings and follow-ups.',
            '**Tasks / To Do**: to add the action items we extract to your task list, keep their completion status in sync, and delete them at your request. This is limited to the tasks Prinotes creates, not your other tasks.',
            '**Drive / OneDrive**: to save your meeting **audio and transcripts** to your own cloud storage (limited to the files Prinotes creates). **This is on by default** when you grant Drive or OneDrive access; you can decline the permission, turn auto-save off during onboarding, or turn it off at any time in your settings.',
            '**Contacts**: read-only, to suggest attendees and assignees as you type.',
          ],
        },
        {
          t: 'note',
          c: "**Limited Use:** Prinotes's use of information received from Google APIs adheres to the Google API Services User Data Policy, including the **Limited Use** requirements. Our use of Microsoft data likewise follows Microsoft's terms. We do not sell any of your data, including your Google or Microsoft data, and we do not use your Google or Microsoft data for advertising or to train AI models.",
        },
        {
          t: 'p',
          c: '**We do not store copies** of your Google or Microsoft calendar, tasks, drive, or contacts. **Contacts are read live and never stored**: when you type, we read contact names and emails from your account only to show suggestions, and keep no copy. We store only the minimal references needed to link a meeting to an item you created (for example, an event identifier).',
        },
        {
          t: 'note',
          c: '**Copies in your own cloud storage:** If you connect Google Drive or OneDrive, Prinotes **automatically saves your meeting audio and transcripts** there unless you turn auto-save off. Those files belong to you and live in your own account, which also means they are **outside our control**: deleting a meeting in Prinotes, deleting your account, or the automatic deletion of audio from our systems does **not** delete the copies in your Drive or OneDrive. You must delete those yourself from your Google or Microsoft account.',
        },
        {
          t: 'p',
          c: 'You can turn auto-save off at any time in your Prinotes settings, or decline the Drive/OneDrive permission entirely, in which case no files are ever sent there.',
        },
        {
          t: 'p',
          c: "You can disconnect a connected account at any time in your Prinotes settings, or revoke access directly from your Google or Microsoft account's security settings. When you disconnect, we delete the stored access tokens for that account.",
        },
      ],
    },
    {
      id: 'transfers',
      n: '11',
      title: 'International Data Transfers',
      blocks: [
        {
          t: 'p',
          c: 'Your account and meeting data are stored on infrastructure located in the **Kingdom of Saudi Arabia**, **encrypted at rest in both locations**: primary data in Riyadh and encrypted backups in Jeddah, both within the Kingdom.',
        },
        {
          t: 'p',
          c: 'To transcribe and summarize your meetings, your content is transferred **outside the Kingdom** to our AI providers (located in the United States or operating globally). These transfers are carried out in accordance with the Saudi Regulation on Personal Data Transfer Outside the Kingdom, are limited to the minimum data necessary to provide the feature, and are covered by data-processing agreements with each provider.',
        },
        {
          t: 'p',
          c: 'Wherever your data is processed, your rights under the PDPL continue to apply and remain enforceable.',
        },
      ],
    },
    {
      id: 'retention',
      n: '12',
      title: 'Data Storage, Retention & Deletion',
      blocks: [
        {
          t: 'p',
          c: 'We keep personal data only for as long as it is needed for the purpose it was collected, or as required by law.',
        },
        {
          t: 'table',
          head: ['Data', 'Retention'],
          rows: [
            [
              'Meeting audio (recordings & uploads)',
              '**Deleted immediately after processing completes. Kept only if you have turned on audio retention***',
            ],
            ['Transcripts, summaries & action items', 'Kept until you delete the meeting or your account'],
            ['Account data', 'Kept while your account is active'],
            ['Billing & tax records', 'Kept as required by applicable accounting and tax law'],
            ['Security & audit logs', 'Kept for about 12 months, then deleted (including from backups)'],
            ['Operational & error logs', 'Kept only as long as needed for security, debugging, and legal purposes'],
          ],
        },
        {
          t: 'p',
          c: '* Kept only if you are on a paid plan and have turned on audio retention, for the period included in your plan. See our [Pricing page](/en/pricing) for the retention included with each plan.',
        },
        { t: 'h', c: 'Deleting your data' },
        {
          t: 'p',
          c: 'You can delete a meeting at any time. When you delete your account, your meetings, transcripts, files, and recordings are **erased from our live systems**, and your account is deactivated with your display name removed. For abuse-prevention and legal compliance, we retain a limited record: your email address, your profile-picture link, the sign-in provider you used (Google or Microsoft), and basic account timestamps (when your account was created and last signed in), together with a security audit entry.',
        },
        {
          t: 'p',
          c: 'When you delete your data, it is removed from our live systems immediately and **purged from our encrypted backups within 30 days**.',
        },
        {
          t: 'note',
          c: '**How to delete or export your data:** You can download a copy of your data or permanently delete your account at any time from your account settings, under **Privacy** (Download my data / Delete account). If anything goes wrong or you need help with a request, email [privacy@prinotes.com](mailto:privacy@prinotes.com)',
        },
      ],
    },
    {
      id: 'your-rights',
      n: '13',
      title: 'Your Rights',
      blocks: [
        { t: 'p', c: 'Under the PDPL, you have the right to:' },
        {
          t: 'ul',
          c: [
            '**Be informed** of how and why your personal data is processed.',
            '**Access** the personal data we hold about you.',
            '**Obtain a copy** of your data in a readable, machine-readable format.',
            '**Correct** data that is inaccurate, incomplete, or out of date.',
            '**Delete** your data when it is no longer needed.',
            '**Withdraw consent** at any time, where processing is based on consent.',
          ],
        },
        {
          t: 'p',
          c: 'You can download your data in two parts: a machine-readable **JSON file** with your profile, meeting details, and action items; and, because transcripts, summaries, and recordings are not included in that file, you can **export those from each meeting as DOCX or PDF**.',
        },
        {
          t: 'p',
          c: 'To exercise these rights, use the controls in your account settings under **Privacy** (download your data or delete your account), or email [privacy@prinotes.com](mailto:privacy@prinotes.com) for any other request or if something goes wrong. We will respond within **30 days**, which may be extended by a further 30 days where a request requires disproportionate effort or is one of several requests, in which case we will notify you of the extension and its reasons.',
        },
        {
          t: 'p',
          c: 'If you believe your rights have not been respected, you may lodge a complaint with **SDAIA**, the competent authority for personal data protection in the Kingdom of Saudi Arabia.',
        },
      ],
    },
    {
      id: 'cookies',
      n: '14',
      title: 'Cookies & Similar Technologies',
      blocks: [
        { t: 'p', c: 'We use **only essential cookies** that are strictly necessary to run the Service:' },
        {
          t: 'ul',
          c: [
            'A **session cookie** to keep you signed in.',
            'A short-lived **security cookie** used during the sign-in process.',
            'Our security and content-delivery provider (**Cloudflare**) may set a strictly necessary security cookie if your visit triggers a security check (for example, during an attack on our Service). It is not used for tracking.',
          ],
        },
        {
          t: 'p',
          c: "The application also uses your browser's local storage to remember interface preferences such as language and theme. This never contains tracking identifiers and stays on your device.",
        },
        {
          t: 'p',
          c: 'We do **not** use advertising or analytics cookies. Our website sets no tracking cookies and self-hosts its fonts, so no data is shared with third-party font or analytics services for that purpose.',
        },
        {
          t: 'p',
          c: 'Because we use only essential cookies, no cookie-consent banner is required. You can control or clear cookies through your browser, but disabling essential cookies may prevent the Service from working.',
        },
      ],
    },
    {
      id: 'security',
      n: '15',
      title: 'Data Security',
      blocks: [
        {
          t: 'p',
          c: 'We use reasonable technical and organizational measures to protect your data, including:',
        },
        {
          t: 'ul',
          c: [
            '**Encryption in transit** using TLS 1.2 and 1.3, and **encryption at rest** using AES-256; connected-account tokens are additionally encrypted at the application layer.',
            '**Encrypted backups**, encrypted before they leave our servers, with decryption keys held separately and offline.',
            '**Per-tenant data isolation** enforced at the database level, and least-privilege access controls.',
            '**Rate limiting and a web application firewall**, plus audit logging of access to data.',
          ],
        },
        {
          t: 'note',
          c: '**Please note:** No method of transmission or storage is completely secure. While we work hard to protect your data, we cannot guarantee absolute security.',
        },
        {
          t: 'p',
          c: '**If something goes wrong.** If a personal data breach occurs that may harm you or affect your rights, we will notify SDAIA within 72 hours of becoming aware of it and will inform you without undue delay, in clear language, including what happened, the potential risks, and our recommendations to protect yourself.',
        },
      ],
    },
    {
      id: 'who-can-access',
      n: '16',
      title: 'Who Can Access Your Data',
      blocks: [
        {
          t: 'p',
          c: 'Access to your data is limited to authorized Priyora staff who need it to operate, support, or secure the Service.',
        },
        {
          t: 'note',
          c: '**Staff access is limited and audited:** access by authorized Priyora staff to your meeting content requires a time-limited access session (up to one hour), used for support and security purposes only, and every such session is fully audited.',
        },
        {
          t: 'p',
          c: 'We never sell your data, and we never use your meeting content for advertising or to build profiles about you.',
        },
      ],
    },
    {
      id: 'childrens-privacy',
      n: '17',
      title: "Children's Privacy",
      blocks: [
        {
          t: 'p',
          c: 'The Service is intended for users aged **18 and over**. We do not knowingly collect personal data from anyone under 18. If you believe a minor has provided us with personal data, contact [privacy@prinotes.com](mailto:privacy@prinotes.com) and we will delete it.',
        },
      ],
    },
    {
      id: 'changes',
      n: '18',
      title: 'Changes to This Policy',
      blocks: [
        {
          t: 'p',
          c: 'We may update this Policy from time to time. Where a change involves a new processing purpose that requires your consent under the PDPL, we will ask for that consent separately before the change applies to you. When we do, we will revise the "Last updated" date above and, for material changes, provide additional notice. Your continued use of the Service after an update means you accept the revised Policy.',
        },
      ],
    },
    {
      id: 'contact',
      n: '19',
      title: 'Contact Us',
      blocks: [
        {
          t: 'p',
          c: 'For privacy questions or to exercise your rights: [privacy@prinotes.com](mailto:privacy@prinotes.com)',
        },
        { t: 'p', c: 'For general support: [support@prinotes.com](mailto:support@prinotes.com)' },
        { t: 'p', c: IDENTITY },
      ],
    },
  ],
};

const TERMS: LegalDoc = {
  key: 'terms',
  eyebrow: 'Legal',
  title: 'Terms of Service',
  updated: 'Last updated 28 July 2026',
  intro:
    'These Terms of Service govern your access to and use of Prinotes, operated by Priyora Company. By creating an account or using the Service, you agree to these Terms. Please read them carefully, together with our Privacy Policy.',
  sections: [
    {
      id: 'acceptance',
      n: '01',
      title: 'Acceptance of Terms',
      blocks: [
        {
          t: 'p',
          c: 'By accessing or using Prinotes (the "Service"), you agree to be bound by these Terms of Service ("Terms") and by our Privacy Policy. If you do not agree, do not use the Service.',
        },
        {
          t: 'p',
          c: 'If you use the Service on behalf of an organization, you represent that you have the authority to bind that organization to these Terms, and "you" refers to that organization.',
        },
      ],
    },
    {
      id: 'eligibility',
      n: '02',
      title: 'Eligibility',
      blocks: [
        {
          t: 'p',
          c: 'You must be at least **18 years old** and have the legal capacity to enter into a contract to use the Service. You must not use the Service if you are barred from doing so under any applicable law.',
        },
      ],
    },
    {
      id: 'description',
      n: '03',
      title: 'Description of the Service',
      blocks: [
        {
          t: 'p',
          c: 'Prinotes helps you capture and organize meetings. It records or accepts uploaded meetings, transcribes them, and uses AI to generate summaries, minutes, decisions, and action items. It can optionally sync with your Google Workspace or Microsoft 365 calendar, tasks, files, and contacts.',
        },
        {
          t: 'p',
          c: 'We are continually improving the Service and may add, change, or remove features. We will try to give reasonable notice of significant changes.',
        },
      ],
    },
    {
      id: 'accounts',
      n: '04',
      title: 'Accounts & Security',
      blocks: [
        {
          t: 'p',
          c: 'You sign in using your Google or Microsoft account. You are responsible for maintaining the security of the account you use to access Prinotes and for all activity that occurs under your account.',
        },
        {
          t: 'p',
          c: "If your organization uses Prinotes, an administrator may manage members, control settings, and access workspace data associated with the organization's account. Notify us promptly at [support@prinotes.com](mailto:support@prinotes.com) of any unauthorized use.",
        },
      ],
    },
    {
      id: 'recording-consent',
      n: '05',
      title: 'Recording Consent: Your Responsibility',
      blocks: [
        {
          t: 'note',
          c: '**This clause is important, please read it carefully:** Prinotes provides no tools that notify meeting participants or collect their consent. Obtaining any legally required consent is entirely your responsibility.',
        },
        {
          t: 'p',
          c: "Many jurisdictions, including the Kingdom of Saudi Arabia and other countries where you or meeting participants may be located, restrict or condition the recording of conversations and the processing of participants' voices or personal data. Some jurisdictions require the consent of **all** participants.",
        },
        {
          t: 'p',
          c: 'In the Kingdom of Saudi Arabia, recording private conversations without the required consent may violate applicable laws, including laws that carry criminal penalties. Similar or stricter rules apply in other countries.',
        },
        {
          t: 'p',
          c: "You are solely responsible for determining whether consent or notice is required for each meeting you record, and for obtaining any required consent from, and providing any required notice to, **all participants** before recording, transcribing, or processing a meeting, including where Prinotes' AI features generate summaries, action items, or other outputs from that meeting. You also agree to inform all participants that the meeting is being recorded and processed by Prinotes.",
        },
        {
          t: 'p',
          c: 'By initiating a recording through Prinotes, you represent that you have obtained all necessary consents and permissions, or that recording is otherwise lawful in the circumstances. You agree to indemnify Priyora against any claim arising from your failure to obtain such consent or provide such notice.',
        },
      ],
    },
    {
      id: 'ai-content',
      n: '06',
      title: 'AI-Generated Content & Accuracy',
      blocks: [
        {
          t: 'note',
          c: '**No reliance without review:** AI outputs are generated automatically and may be incomplete, inaccurate, out of context, or attributed to the wrong speaker. They are not a certified or verbatim record of a meeting.',
        },
        {
          t: 'p',
          c: 'Prinotes uses artificial intelligence to transcribe audio and generate summaries, minutes, decisions, and action items ("AI Outputs"). We make no warranty as to the completeness, accuracy, or reliability of any AI Output.',
        },
        {
          t: 'p',
          c: 'AI Outputs are provided for convenience only and **do not constitute legal, financial, medical, or other professional advice**. You are solely responsible for reviewing AI Outputs before distributing them, acting on them, or relying on them for any decision or commitment. To the fullest extent permitted by law, Priyora disclaims all liability arising from actions taken in reliance on unreviewed AI Outputs.',
        },
      ],
    },
    {
      id: 'your-content',
      n: '07',
      title: 'Your Content & License to Prinotes',
      blocks: [
        {
          t: 'p',
          c: 'You retain all ownership of the content you record, upload, or create through the Service ("Your Content").',
        },
        {
          t: 'p',
          c: 'To provide the Service, you grant Priyora a limited, non-exclusive, worldwide license to host, store, process, transcribe, summarize, and display Your Content **solely to operate and provide the Service to you**. This license ends when you delete Your Content or your account, except for copies retained transiently in backups as described in the Privacy Policy.',
        },
        {
          t: 'p',
          c: '**Meeting audio is deleted from our systems as soon as processing finishes, and is kept only if you have turned on audio retention.*** You can turn retention off or delete retained audio at any time. Retention does not apply retroactively to audio already deleted.',
        },
        {
          t: 'p',
          c: '* Kept only if you are on a paid plan and have turned on audio retention, for the period included in your plan. See our [Pricing page](/en/pricing) for the retention included with each plan.',
        },
        {
          t: 'p',
          c: 'If you connect Google Drive or OneDrive, Prinotes automatically saves your meeting audio and transcripts to your own cloud storage unless you turn that off. Those copies are yours and sit outside our control: deleting content in Prinotes does not remove them, and you are responsible for managing them in your own Google or Microsoft account.',
        },
        {
          t: 'p',
          c: 'You represent and warrant that you have all rights necessary to submit Your Content and to grant this license, and that Your Content does not violate any law or the rights of any third party.',
        },
      ],
    },
    {
      id: 'acceptable-use',
      n: '08',
      title: 'Acceptable Use & Prohibited Activities',
      blocks: [
        { t: 'p', c: 'You agree not to:' },
        {
          t: 'ul',
          c: [
            'Use the Service for any unlawful purpose or in violation of any applicable law or regulation.',
            'Record, transcribe, or process any meeting without the consent required under Section 5.',
            'Infringe the intellectual property, privacy, or other rights of any person.',
            'Upload malicious code, or attempt to gain unauthorized access to, disrupt, or compromise the Service or its security.',
            'Reverse engineer, decompile, or attempt to extract the source code of the Service, except where permitted by law.',
            'Resell, sublicense, or commercially exploit the Service without our prior written permission.',
          ],
        },
      ],
    },
    {
      id: 'third-party',
      n: '09',
      title: 'Third-Party Services',
      blocks: [
        {
          t: 'p',
          c: 'The Service integrates with third-party services such as Google, Microsoft, and (for payments) Moyasar. Your use of those services is governed by their own terms and privacy policies.',
        },
        {
          t: 'p',
          c: 'Connecting a third-party service is your choice. We are not responsible for the availability, accuracy, or practices of third-party services, and their changes may affect Prinotes features that rely on them.',
        },
      ],
    },
    {
      id: 'billing',
      n: '10',
      title: 'Subscriptions & Billing',
      blocks: [
        {
          t: 'p',
          c: 'Prinotes offers a **free plan** and **paid plans**. The plans, their features, and their prices are shown on our [Pricing page](/en/pricing).',
        },
        { t: 'h', c: 'Fees and taxes' },
        {
          t: 'ul',
          c: [
            'Fees are stated in **Saudi Riyal (SAR)** and shown to you in full before you confirm a purchase.',
            '**No value-added tax (VAT)** is charged at this time. VAT will only be applied if and when Priyora becomes VAT-registered, and any change will be shown clearly.',
          ],
        },
        { t: 'h', c: 'Billing and renewal' },
        {
          t: 'ul',
          c: [
            'Paid plans are billed in advance on a **monthly or annual** cycle, whichever you choose at checkout, and **renew automatically** at the end of each cycle until you cancel.',
            'We charge the payment method on file on each renewal date. If a charge fails, we may retry it and may suspend paid features until payment succeeds.',
            'We will give you notice before any price change takes effect, and the new price applies from your next renewal.',
          ],
        },
        { t: 'h', c: 'Cancellation' },
        {
          t: 'ul',
          c: [
            'You can cancel at any time from your account settings. Cancellation stops the next renewal; your paid features remain available until the end of the period you have already paid for.',
            'When a paid plan ends, audio retained under that plan is deleted after a 30-day grace period. Your transcripts and notes are not affected.',
          ],
        },
        { t: 'h', c: 'Refunds' },
        {
          t: 'note',
          c: '**Before you subscribe:** A paid plan can only be refunded if it is **completely unused**. As soon as you process a meeting or use a single credit, the seven-day cancellation right no longer applies and the payment is final.',
        },
        {
          t: 'ul',
          c: [
            'Under the Saudi E-Commerce Law, you may cancel a paid plan and receive a **full refund within seven (7) days** of subscribing, **provided you have not used the paid features at all** during that period.',
            '**Using any part of your plan ends this right.** Processing a single meeting, consuming a single credit, or otherwise obtaining any benefit from the paid features means the plan has been used, and the subscription is **no longer refundable**. This applies however small the usage is: there is no partial or pro-rata refund for unused credits or unused time.',
            '**Renewal payments are non-refundable**, except where the Service was unavailable or did not work as described.',
            'Where a refund is due, we return it **using the same payment method you paid with, within 15 days**.',
            'Outside these cases, we still review every request and will refund at our discretion where something went wrong on our side. Write to us at [support@prinotes.com](mailto:support@prinotes.com)',
            'Nothing in these Terms limits any right you have under the Saudi E-Commerce Law or its Implementing Regulations.',
          ],
        },
        { t: 'h', c: 'Payments' },
        {
          t: 'ul',
          c: [
            'Payments are processed by **Moyasar**, a payment provider licensed in the Kingdom of Saudi Arabia. **Prinotes never stores your card details**: Moyasar tokenizes your payment data, and we retain only the card brand and last four digits.',
            'Invoices and payment history are available in your account settings.',
          ],
        },
      ],
    },
    {
      id: 'ip',
      n: '11',
      title: 'Intellectual Property',
      blocks: [
        {
          t: 'p',
          c: 'The Service, including its software, design, branding, and content (other than Your Content), is owned by Priyora Company and protected by intellectual property laws. We grant you a limited, non-exclusive, non-transferable right to use the Service in accordance with these Terms. No other rights are granted.',
        },
      ],
    },
    {
      id: 'feedback',
      n: '12',
      title: 'Feedback',
      blocks: [
        {
          t: 'p',
          c: 'If you send us suggestions or feedback about the Service, you grant Priyora a perpetual, irrevocable, royalty-free license to use it without restriction or obligation to you.',
        },
      ],
    },
    {
      id: 'disclaimers',
      n: '13',
      title: 'Disclaimers',
      blocks: [
        {
          t: 'p',
          c: 'The Service is provided on an **"as is" and "as available"** basis. To the fullest extent permitted by law, we disclaim all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or that AI Outputs will be accurate.',
        },
      ],
    },
    {
      id: 'liability',
      n: '14',
      title: 'Limitation of Liability',
      blocks: [
        {
          t: 'p',
          c: 'To the fullest extent permitted by law, Priyora will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, profits, or revenue, arising from your use of the Service.',
        },
        {
          t: 'p',
          c: 'Our total aggregate liability arising from or relating to the Service will not exceed the greater of (a) the total amount you paid us for the Service in the twelve months before the event giving rise to the claim, or (b) SAR 500.',
        },
        {
          t: 'p',
          c: 'Nothing in these Terms excludes or limits liability for fraud, willful misconduct, gross negligence, or any liability that cannot be excluded or limited under the laws of the Kingdom of Saudi Arabia.',
        },
      ],
    },
    {
      id: 'indemnification',
      n: '15',
      title: 'Indemnification',
      blocks: [
        {
          t: 'p',
          c: 'You agree to indemnify and hold harmless Priyora Company and its staff from any claim, loss, or expense (including reasonable legal fees) arising from your use of the Service, Your Content, your failure to obtain any required recording consent, or your breach of these Terms.',
        },
      ],
    },
    {
      id: 'termination',
      n: '16',
      title: 'Suspension & Termination',
      blocks: [
        {
          t: 'p',
          c: 'You may stop using the Service and delete your account at any time. We may suspend or terminate your access if you breach these Terms, if required by law, or to protect the Service or its users.',
        },
        {
          t: 'p',
          c: 'On termination, your right to use the Service ends. For 30 days after termination (unless we are legally required to delete sooner), you may request an export of Your Content as described in the Privacy Policy. Provisions that by their nature should survive termination, including intellectual property, disclaimers, limitation of liability, and indemnification, will survive.',
        },
      ],
    },
    {
      id: 'governing-law',
      n: '17',
      title: 'Governing Law & Dispute Resolution',
      blocks: [
        { t: 'p', c: 'These Terms are governed by the laws of the **Kingdom of Saudi Arabia**.' },
        {
          t: 'p',
          c: 'If a dispute arises, the parties will first try in good faith to resolve it through negotiation. If it cannot be resolved, the dispute will be subject to the exclusive jurisdiction of the **competent courts of Jeddah**, Kingdom of Saudi Arabia.',
        },
      ],
    },
    {
      id: 'changes',
      n: '18',
      title: 'Changes to These Terms',
      blocks: [
        {
          t: 'p',
          c: 'We may update these Terms from time to time. When we do, we will revise the "Last updated" date above and, for material changes, provide additional notice. Your continued use of the Service after an update means you accept the revised Terms.',
        },
      ],
    },
    {
      id: 'contact',
      n: '19',
      title: 'Contact Us',
      blocks: [
        { t: 'p', c: 'Questions about these Terms? Contact [support@prinotes.com](mailto:support@prinotes.com)' },
        { t: 'p', c: IDENTITY },
      ],
    },
  ],
};

const SUBPROCESSORS: LegalDoc = {
  key: 'subprocessors',
  eyebrow: 'Legal',
  title: 'Sub-processors',
  updated: 'Last updated 28 July 2026',
  intro:
    'To provide Prinotes, Priyora Company works with a small number of trusted third parties that process personal data on our behalf. We hold each of them to contractual obligations to protect your data and to process it only on our instructions. This page lists our current sub-processors and is kept up to date as they change.',
  sections: [
    {
      id: 'infrastructure',
      n: '01',
      title: 'Infrastructure & Hosting',
      blocks: [
        {
          t: 'table',
          head: ['Sub-processor', 'Purpose', 'Data processed', 'Location'],
          rows: [
            [
              'Oracle Cloud Infrastructure (OCI)',
              'Cloud hosting, database, and encrypted object storage',
              'All account and meeting data at rest',
              'Saudi Arabia (Riyadh; backups in Jeddah)',
            ],
            [
              'Cloudflare',
              'Content delivery, TLS, and security (firewall / bot protection)',
              'IP address and connection metadata',
              'United States / global',
            ],
            [
              'Sentry',
              'Error and performance monitoring',
              'Diagnostic error data (configured to minimize and filter personal data before it is sent)',
              'European Union / United States',
            ],
          ],
        },
      ],
    },
    {
      id: 'ai-providers',
      n: '02',
      title: 'AI Providers',
      blocks: [
        {
          t: 'p',
          c: 'We use the providers below, collectively, for transcription, structuring, and summaries. To protect our product, we do not break down which provider performs which specific function.',
        },
        {
          t: 'table',
          head: ['Sub-processor', 'Purpose', 'Data processed', 'Location'],
          rows: [
            ['OpenAI', 'AI processing of meeting content', 'Meeting audio and/or transcript text', 'United States'],
            [
              'Google Gemini',
              'AI processing of meeting content',
              'Meeting audio and/or transcript text',
              'United States / global',
            ],
          ],
        },
        {
          t: 'note',
          c: '**How your content is handled:** Our AI providers may retain content for a short period (approximately 30 days*) **solely to detect abuse and keep their services safe**, after which it is deleted. They do not use it to train their models, and we do not retain or use your raw audio to train or improve our own models. **On our own systems, meeting audio is deleted as soon as processing finishes, and is kept only if you have turned on audio retention.**†',
        },
        {
          t: 'p',
          c: '* Based on the published API data-usage policies of our AI providers (OpenAI and Google Gemini, paid API tiers), which do not use customer content for training. Exact windows are set by each provider and may change.',
        },
        {
          t: 'p',
          c: '† Kept only if you are on a paid plan and have turned on audio retention, for the period included in your plan. See our [Pricing page](/en/pricing) for the retention included with each plan.',
        },
      ],
    },
    {
      id: 'connected-accounts',
      n: '03',
      title: 'Connected Accounts (only when you connect them)',
      blocks: [
        {
          t: 'p',
          c: "The following are used only if you choose to connect them, and only for the features you enable. Your use of these services is also governed by the providers' own terms.",
        },
        {
          t: 'table',
          head: ['Sub-processor', 'Purpose', 'Data processed', 'Location'],
          rows: [
            [
              'Google Workspace',
              'Calendar, Tasks, Drive, and Contacts sync',
              'Calendar events, tasks, files you create, contacts (read live)',
              'United States / global',
            ],
            [
              'Microsoft 365 (Graph)',
              'Calendar, To Do, OneDrive, and Contacts sync',
              'Calendar events, tasks, files you create, contacts (read live)',
              'United States / global',
            ],
          ],
        },
      ],
    },
    {
      id: 'payments',
      n: '04',
      title: 'Payments',
      blocks: [
        {
          t: 'table',
          head: ['Sub-processor', 'Purpose', 'Data processed', 'Location'],
          rows: [
            [
              'Moyasar',
              'Payment processing for paid subscriptions',
              'Name, email, and payment details (card data is handled and tokenized by Moyasar; Prinotes stores only card brand and last four digits)',
              'Saudi Arabia',
            ],
          ],
        },
      ],
    },
    {
      id: 'changes',
      n: '05',
      title: 'Changes to This List',
      blocks: [
        {
          t: 'p',
          c: 'We update this page whenever we add or remove a sub-processor. The "Last updated" date above reflects the most recent change. For questions about our sub-processors, contact [privacy@prinotes.com](mailto:privacy@prinotes.com)',
        },
      ],
    },
  ],
};

export const LEGAL_DOCS_EN: Record<LegalDoc['key'], LegalDoc> = {
  privacy: PRIVACY,
  terms: TERMS,
  subprocessors: SUBPROCESSORS,
};
